import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/hooks/useAuth';
import { useApiProviders } from '@/hooks/useApiProviders';
import { useUserIntegrations } from '@/hooks/useApiIntegrations';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, Loader2, Play, ArrowRightLeft } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { apiClient } from '@/services/apiClient';

const formSchema = z.object({
  providerId: z.string().min(1, 'Provider is required'),
  endpointId: z.string().optional(),
  customPath: z.string().optional(),
  method: z.string().optional(),
  params: z.string().optional(),
  body: z.string().optional(),
});

export function ApiTester() {
  const { user } = useAuth();
  const { data: providers = [] } = useApiProviders();
  const { data: integrations = [] } = useUserIntegrations(user?.id);
  
  const [activeTab, setActiveTab] = useState('request');
  const [requestType, setRequestType] = useState<'endpoint' | 'custom'>('endpoint');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      providerId: '',
      endpointId: '',
      customPath: '',
      method: 'GET',
      params: '{}',
      body: '{}',
    },
  });
  
  const selectedProviderId = form.watch('providerId');
  const selectedProvider = providers.find(p => p.id === selectedProviderId);
  
  const hasConnection = !!integrations.find(i => 
    i.provider_id === selectedProviderId && 
    i.connection_status === 'connected'
  );
  
  const availableEndpoints = selectedProvider?.endpoints || [];
  
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    setResponse(null);
    setError(null);
    
    try {
      let params = {};
      let body = {};
      
      try {
        if (data.params) {
          params = JSON.parse(data.params);
        }
      } catch (e) {
        setError('Invalid JSON in params field');
        setIsLoading(false);
        return;
      }
      
      try {
        if (data.body) {
          body = JSON.parse(data.body);
        }
      } catch (e) {
        setError('Invalid JSON in body field');
        setIsLoading(false);
        return;
      }
      
      const requestData = {
        providerId: data.providerId,
        params,
        body,
      };
      
      if (requestType === 'endpoint' && data.endpointId) {
        Object.assign(requestData, { endpointId: data.endpointId });
      } else {
        Object.assign(requestData, { 
          path: data.customPath,
          method: data.method,
        });
      }
      
      const result = await apiClient.makeRequest(requestData);
      setResponse(result);
      setActiveTab('response');
    } catch (err: any) {
      setError(`Request failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const connectableProviders = providers.filter(provider => {
    return integrations.some(integration => 
      integration.provider_id === provider.id &&
      integration.connection_status === 'connected'
    );
  });
  
  if (connectableProviders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>API Tester</CardTitle>
          <CardDescription>
            Test your API connections and endpoints
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No connected APIs</AlertTitle>
            <AlertDescription>
              You don't have any active API connections. Please connect to a provider first.
            </AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => window.history.back()}>
            Go Back
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>API Tester</CardTitle>
        <CardDescription>
          Test your API connections and endpoints
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="request">Request</TabsTrigger>
            <TabsTrigger value="response">Response</TabsTrigger>
          </TabsList>
          
          <TabsContent value="request">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="providerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>API Provider</FormLabel>
                      <Select 
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.setValue('endpointId', '');
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select provider" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {connectableProviders.map(provider => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {!selectedProviderId && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Select a provider to continue
                    </AlertDescription>
                  </Alert>
                )}
                
                {selectedProviderId && !hasConnection && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>No active connection</AlertTitle>
                    <AlertDescription>
                      You don't have an active connection to this provider.
                    </AlertDescription>
                  </Alert>
                )}
                
                {selectedProviderId && hasConnection && (
                  <>
                    <div className="flex items-center space-x-4 rounded-md border p-4">
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium leading-none">
                          Request Type
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Choose how to make your API request
                        </p>
                      </div>
                      <Tabs value={requestType} onValueChange={(value: any) => setRequestType(value)}>
                        <TabsList>
                          <TabsTrigger value="endpoint">Use Endpoint</TabsTrigger>
                          <TabsTrigger value="custom">Custom Request</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>
                    
                    {requestType === 'endpoint' ? (
                      <FormField
                        control={form.control}
                        name="endpointId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Endpoint</FormLabel>
                            <Select 
                              onValueChange={field.onChange}
                              value={field.value || ''}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select endpoint" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableEndpoints.map(endpoint => (
                                  <SelectItem key={endpoint.id} value={endpoint.id}>
                                    {endpoint.name} ({endpoint.method})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Pre-configured endpoints for this provider
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : (
                      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
                        <div className="md:col-span-2">
                          <FormField
                            control={form.control}
                            name="customPath"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>API Path</FormLabel>
                                <FormControl>
                                  <Input placeholder="/resource/path" {...field} />
                                </FormControl>
                                <FormDescription>
                                  Path relative to base URL: {selectedProvider?.base_url}
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        
                        <FormField
                          control={form.control}
                          name="method"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Method</FormLabel>
                              <Select 
                                onValueChange={field.onChange}
                                value={field.value || 'GET'}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="HTTP method" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="GET">GET</SelectItem>
                                  <SelectItem value="POST">POST</SelectItem>
                                  <SelectItem value="PUT">PUT</SelectItem>
                                  <SelectItem value="PATCH">PATCH</SelectItem>
                                  <SelectItem value="DELETE">DELETE</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                    
                    <FormField
                      control={form.control}
                      name="params"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Query Parameters (JSON)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder='{
  "param1": "value1",
  "param2": "value2"
}'
                              className="font-mono min-h-[100px]"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Request parameters as JSON object
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="body"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Request Body (JSON)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder='{
  "data": {
    "attribute": "value"
  }
}'
                              className="font-mono min-h-[100px]"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Request body as JSON object (for POST/PUT/PATCH)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="flex justify-end">
                  <Button type="submit" disabled={isLoading || !hasConnection}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Execute Request
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </TabsContent>
          
          <TabsContent value="response">
            <div className="mb-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setActiveTab('request')}
              >
                <ArrowRightLeft className="mr-2 h-4 w-4" />
                Return to Request
              </Button>
            </div>
            
            {response ? (
              <div className="border rounded-md p-4">
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <Badge variant={response.status < 400 ? 'outline' : 'destructive'}>
                      Status: {response.status} {response.statusText}
                    </Badge>
                  </div>
                </div>
                
                <h3 className="text-sm font-semibold mb-2">Response Body:</h3>
                <pre className="bg-muted p-4 rounded-md overflow-auto max-h-[400px] text-xs">
                  {JSON.stringify(response.data, null, 2)}
                </pre>
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <div className="text-center p-6 border rounded-md">
                <p className="text-muted-foreground">
                  Execute a request to see the results here
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
